import React, { FC, useEffect, useRef, useState } from 'react'
import { Box, Typography, Grid, Chip, TextField, useTheme } from '@mui/material'

import {
  Value,
  Platform,
  TaskType,
  Channel,
  OrgRow,
} from './taskTypes'
import TaskTypeTwoPane from './TaskTypeTwoPane'
import TaskTypeDatePicker from './TaskTypeDatePicker'
import { ORG_TWO_PANE_TREE } from './form_config'
import { OrganizationTableItem, User } from 'types'
import { RowShell, OrgRowContainer, BoxRow, TaskRadio, denseInput } from './style'
import RowActionComponent from './RowActionComponent'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import { useDrawer } from 'context/TaskDrawerContext/DrawerContext'
import TaskOrgAutocomplete from './TaskOrgAutocomplete'
import GlobalUserSelect from './GlobalUserSelect'
import { calculateDateDue } from './taskUtil'
import { Dayjs } from 'dayjs'

type DescCtx = { kind: 'prod'; idx: number } | { kind: 'org'; idx: number } | null

interface OrgRowsProps {
  orgs: OrganizationTableItem[]
  orgRows: OrgRow[]
  onUpdateRow: (idx: number, patch: Partial<OrgRow>) => void
  onRemoveRow: (idx: number) => void
  onChangeOrg: (idx: number, org: any) => void
  onClearOrg: (idx: number) => void
  filteredUsers: User[]
  onChangeType: (val: Value | null, row: OrgRow, idx: number) => void
  buildTaskName: (
    _orgName: string,
    taskType: string,
    taskTypeLabel: string,
    _platform?: string,
    _channel?: string
  ) => string
  openEditor: (ctx: DescCtx, initial: string) => void
  cloneFrom: (srcIdx: number) => void
}
const OrgRows: FC<OrgRowsProps> = ({
  orgs,
  orgRows,
  onUpdateRow,
  onRemoveRow,
  onChangeOrg,
  onClearOrg,
  onChangeType,
  filteredUsers,
  buildTaskName,
  openEditor,
  cloneFrom,
}) => {
  const theme = useTheme()
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)
  const { sel, open: drawerOpen, select, closeDrawer } = useDrawer()
  const hoverTimerRef = useRef<number | null>(null)

  // helpers: arm/disarm the "active" row with a small delay on leave
  const arm = (i: number) => {
    if (hoverTimerRef.current) {
      window.clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    setHoverIdx(i)
  }
  const disarm = (i: number) => {
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = window.setTimeout(() => {
      setHoverIdx(prev => (prev === i ? null : prev))
    }, 140) // 120–180ms feels good
  }

  // optional: ESC to unpin
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape'
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  const changeOrgPlatform = (idx: number, platform: Platform | '') => {
    onUpdateRow(idx, { platform, channel: '', taskType: '' as TaskType | '', taskTypeLabel: '', addtlNotes: '' })
  }
  const changeOrgChannel = (idx: number, channel: Channel | '') => {
    onUpdateRow(idx, { channel, taskType: '' as TaskType | '', taskTypeLabel: '', addtlNotes: '' })
  }
  const updateStartDate = (idx: number, date: Dayjs | null, row: OrgRow) => {
    const newDateDue = calculateDateDue(row?.taskType ?? '', date)
    onUpdateRow(idx, { startDate: date ?? null, dateDue: newDateDue ?? null })
  }
  const rowSx = {
    ...denseInput,
    width: '100%',
    '& .MuiInputBase-root': { minHeight: 40, maxHeight: 40, bgcolor: 'background.section' },
  }

  const userById = React.useMemo(() => {
    const m = new Map<string, { label: string }>()
    filteredUsers.forEach(u => m.set(u.user_uuid, { label: u.name }))
    return m
  }, [filteredUsers])

  const isRowValid = (row: any) =>
    !!(row?.org?.org_uuid && row.platform && row.channel && row.taskType && row.startDate)

  return (
    <Box sx={{ width: '100%', mb: 1.5 }}>
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
        Organization Level
      </Typography>

      {orgRows.map((row, idx) => {
        const isPinned = sel?.kind === 'org' && sel?.idx === idx
        const isHovered = !drawerOpen && hoverIdx === idx
        const isSelectedRow = sel?.kind === 'org' && sel?.idx === idx
        const suppressRow = drawerOpen && !isSelectedRow
        const calcColor = !row?.org?.org_uuid || suppressRow ? theme.palette.text.disabled : theme.palette.text.primary
        const invalid = !isRowValid(row)
        return (
          <React.Fragment key={`org-${idx}`}>
            <RowShell>
              <OrgRowContainer
                key={`org-${idx}`}
                data-active={isHovered ? 'true' : 'false'}
                data-pinned={isPinned ? 'true' : 'false'}
                data-suppress={suppressRow}
              >
                <Box sx={{ pointerEvents: 'auto', opacity: drawerOpen ? 0 : 1 }}>
                  <RowActionComponent
                    row={row}
                    idx={idx}
                    kind={'org'}
                    removeRow={onRemoveRow}
                    openEditor={openEditor}
                    cloneRowFrom={cloneFrom}
                    onHoverStart={() => arm(idx)}
                    onHoverEnd={() => disarm(idx)}
                  />
                </Box>
                <Grid container columns={30} spacing={1} alignItems="center" wrap="nowrap">
                  {/* Organization */}
                  {/* <Grid
                    item
                    xs={6}
                    sx={theme => ({
                      transition: theme.transitions.create(['flex-basis', 'max-width'], {
                        duration: 200,
                        easing: theme.transitions.easing.easeInOut,
                      }),
                    })}
                  >
                    <FormAutocomplete
                      data={orgs}
                      isLoading={false}
                      label="Organization"
                      value={o.org}
                      onChange={(org: any) => onUpdateRow(idx, { org })}
                      onClear={() =>
                        onUpdateRow(idx, { org: null, platform: '', channel: '', taskType: '', taskTypeLabel: '' })
                      }
                      disabled={suppressRow}
                      sx={rowSx}
                      shrinkLabel
                      placeholder="Active organizations"
                      />
                      </Grid> */}
                  <TaskOrgAutocomplete
                    data={orgs}
                    isLoading={false}
                    label="Organization"
                    value={row?.org}
                    onChange={(org: any) => onUpdateRow(idx, { org })}
                    onClear={() =>
                      onUpdateRow(idx, { org: null, platform: '', channel: '', taskType: '', taskTypeLabel: '' })
                    }
                    disabled={suppressRow}
                    sx={rowSx}
                    shrinkLabel
                    placeholder="Active organizations"
                  />

                  {/* Task Type (TwoPane) */}
                  <TaskTypeTwoPane
                    tree={ORG_TWO_PANE_TREE}
                    disabled={!row.org?.org_uuid || suppressRow}
                    value={
                      row.taskType
                        ? {
                            base: row.taskType,
                            sub: row.taskTypeLabel?.split(' ')?.slice(1)?.join(' ') || null,
                            label: row.taskTypeLabel || null,
                          }
                        : null
                    }
                    onChange={(e: Value) => onChangeType(e, row, idx)}
                    sx={{
                      ...rowSx,
                      '& .MuiInputBase-root': { minHeight: 40, maxHeight: 40, backgroundColor: 'background.section' },
                    }}
                    error={Boolean(row?.org?.org_uuid) && !row?.taskType && Boolean(row?.channel)}
                    isPinned={isPinned}
                    isHovered={isHovered}
                  />

                  <GlobalUserSelect
                    name={`assignees-${idx}`}
                    value={row.assignees ?? []}
                    options={filteredUsers}
                    displayLabel="Assignees"
                    onChange={ids => onUpdateRow(idx, { assignees: ids })}
                    disabled={!row?.org?.org_uuid || suppressRow}
                    sx={{
                      ...rowSx,
                      bgcolor: 'background.section',
                      '& .MuiInputBase-root': { minHeight: 40, maxHeight: 40, backgroundColor: 'background.section' },
                    }}
                    isPinned={isPinned}
                    isHovered={isHovered}
                  />
                  {/* Start */}
                  <Grid
                    item
                    xs={isHovered ? 4.8 : 6}
                    sx={theme => ({
                      transition: theme.transitions.create(['flex-basis', 'max-width'], {
                        duration: 200,
                        easing: theme.transitions.easing.easeInOut,
                      }),
                    })}
                  >
                    <TaskTypeDatePicker
                      label="Start"
                      value={row.startDate}
                      onChange={d => updateStartDate(idx, d, row)}
                      disabled={!row?.org?.org_uuid || suppressRow}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: rowSx,
                          error:
                            !row.startDate && !suppressRow && (row.platform || row.channel || row.taskType)
                              ? true
                              : false,
                          placeholder: 'Select start date',
                          inputProps: {
                            sx: {
                              color: calcColor,
                              WebkitTextFillColor: calcColor,
                            },
                          },
                          InputLabelProps: {
                            shrink: true,
                            sx: {
                              color: calcColor,
                              WebkitTextFillColor: calcColor,
                            },
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Due */}
                  <Grid
                    item
                    xs={isHovered ? 4.8 : 6}
                    sx={theme => ({
                      transition: theme.transitions.create(['flex-basis', 'max-width'], {
                        duration: 200,
                        easing: theme.transitions.easing.easeInOut,
                      }),
                    })}
                  >
                    <TaskTypeDatePicker
                      label="Due"
                      value={row.dateDue}
                      onChange={d => onUpdateRow(idx, { dateDue: d })}
                      disabled={!row?.org?.org_uuid || suppressRow}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: rowSx,
                          placeholder: 'Select date due',
                          inputProps: {
                            sx: {
                              color: calcColor,
                              WebkitTextFillColor: calcColor,
                            },
                          },
                          InputLabelProps: {
                            shrink: true,
                            sx: { color: calcColor, WebkitTextFillColor: calcColor },
                          },
                        },
                      }}
                    />
                  </Grid>
                  {/* Details */}
                  <Grid
                    item
                    xs={isHovered ? 5 : 6}
                    sx={theme => ({
                      transition: theme.transitions.create(['flex-basis', 'max-width'], {
                        duration: 200,
                        easing: theme.transitions.easing.easeInOut,
                      }),
                    })}
                  >
                    <TextField
                      label="Details"
                      size="small"
                      value={row?.details ?? ''}
                      onChange={e => onUpdateRow(idx, { details: e.target.value })}
                      disabled={!row?.org?.org_uuid || suppressRow}
                      placeholder="Short summary (e.g., Q1 campaign scaling)"
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        ...rowSx,
                        '& .MuiInputBase-input::placeholder': {
                          opacity: 0.6,
                          fontStyle: 'italic',
                        },
                      }}
                      inputProps={{
                        sx: { color: calcColor, WebkitTextFillColor: calcColor },
                      }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {row?.org?.name && row?.taskType && (
                    <BoxRow data-suppress={suppressRow}>
                      <Typography variant="caption">Name:</Typography>
                      <Typography
                        variant="caption"
                        fontWeight={'bold'}
                        color={invalid ? 'text.disabled' : 'text.highlight'}
                        noWrap
                      >
                        {buildTaskName(row?.org?.name, row.taskType, row.taskTypeLabel)}
                      </Typography>
                    </BoxRow>
                  )}
                  {row?.platform && (
                    <BoxRow data-suppress={suppressRow}>
                      <Typography variant="caption">Platform:</Typography>
                      <Typography
                        variant="caption"
                        fontWeight={'bold'}
                        color={invalid ? 'text.disabled' : 'text.highlight'}
                        noWrap
                      >
                        {row?.platform}
                      </Typography>
                    </BoxRow>
                  )}
                  {row?.channel && (
                    <BoxRow data-suppress={suppressRow}>
                      <Typography variant="caption">Channel:</Typography>
                      <Typography
                        variant="caption"
                        fontWeight={'bold'}
                        sx={{ color: renderColorByString(row?.channel) }}
                        noWrap
                      >
                        {row?.channel}
                      </Typography>
                    </BoxRow>
                  )}
                  {!!row.assignees?.length && (
                    <Box
                      sx={{
                        mt: 0.5,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        filter: suppressRow ? 'grayscale(100%)' : '',
                      }}
                    >
                      {row.assignees.map(id => (
                        <Chip key={id} size="small" label={userById.get(id)?.label ?? id} />
                      ))}
                    </Box>
                  )}
                </Box>
              </OrgRowContainer>
              <TaskRadio
                data-task-radio
                size="small"
                onMouseEnter={() => arm(idx)}
                onMouseLeave={() => disarm(idx)}
                sx={{ pointerEvents: 'auto', cursor: 'pointer', zIndex: 2 }}
                onMouseDown={e => {
                  e.stopPropagation()
                }}
                onClick={e => {
                  e.stopPropagation()
                  select('org', idx)
                  setHoverIdx(null)
                }}
                checked={isPinned}
              />
            </RowShell>
          </React.Fragment>
        )
      })}
    </Box>
  )
}
export default OrgRows
